import { init } from "@airwallex/components-sdk";
import React, { useEffect, useState } from "react";
import { createElement } from "@airwallex/components-sdk";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import type { ElementTypes } from "@airwallex/components-sdk";
import { AirWallexUtils } from "@/utils/ach/airWallex";
import { useAwxCreateAndAuthorizeAccountMutation } from "@common/utils/graphql";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { env, isDevelopment } from "@common/utils";
import { NoResultsCard } from "@common/components";
import { Button } from "@/components/ui/button";
import { Bank } from "@phosphor-icons/react";
import { useToast } from "@/components/ui/use-toast";

const KYCOnboarding: React.FC = () => {
  const { toast } = useToast();
  const [initializingSDK, setInitializingSDK] = useState<boolean>(true);
  const [startKYC, setStartKYC] = useState<boolean>(false);
  const [codeVerifier, setCodeVerifier] = useState<string>("");
  const [retryAttempts, setRetryAttempts] = useState<number>(0);
  const userStore = useUserStore.getState();

  if (!userStore.workEmail) {
    alert("Please provide a work email");
  }

  const [
    createAndAuthorizeAccount,
    { data: authorizationData, loading: authorizationLoading },
  ] = useAwxCreateAndAuthorizeAccountMutation();

  useEffect(() => {
    let element: ElementTypes["kyc"] | null = null;
    if (
      authorizationData?.awxCreateAndAuthorizeAccount.__typename ===
        "MutationAwxCreateAndAuthorizeAccountSuccess" &&
      startKYC
    ) {
      setInitializingSDK(true);
      const authCode =
        authorizationData.awxCreateAndAuthorizeAccount.data.authorizationCode;
      const initializeSDK = async () => {
        try {
          await init({
            authCode,
            codeVerifier,
            env: isDevelopment ? "demo" : "prod",
            clientId: env.airWallexClientId,
          });

          element = await createElement("kyc", {
            hideHeader: true,
            hideNav: true,
          });
          element.mount("onboarding-kyc");

          element.on("ready", (event) => {
            console.log("ready", event);
          });

          element.on("success", (event) => {
            console.log("success", event);
          });

          setInitializingSDK(false);
        } catch (e) {
          // When the SDK fails to initialize, increment retryAttempts to restart the process
          setRetryAttempts((attempts) => attempts + 1);
          return;
        }
      };

      initializeSDK();

      return () => element?.destroy();
    }
  }, [startKYC, authorizationData, codeVerifier, setRetryAttempts]);

  useEffect(() => {
    // Only retry 3 times, after that ask the user to refresh the page
    if (retryAttempts > 3) {
      toast({
        variant: "destructive",
        title:
          "Failed to initialize KYC. Please refresh the page and try again.",
      });
      setInitializingSDK(false);
      return;
    }

    const handleAuthFlow = async () => {
      const verifier = AirWallexUtils.generateCodeVerifier();
      const challenge =
        await AirWallexUtils.generateCodeChallengeFromVerifier(verifier);
      setCodeVerifier(verifier);

      createAndAuthorizeAccount({
        variables: {
          codeChallenge: challenge,
          email: userStore.workEmail!,
        },
      });
    };
    handleAuthFlow();
  }, [createAndAuthorizeAccount, userStore.workEmail, retryAttempts, toast]);

  const isLoading = authorizationLoading || initializingSDK;

  if (startKYC) {
    return (
      <div id="onboarding-kyc" className={"mt-4 h-[576px]"}>
        {isLoading && <FullPageLoader />}
      </div>
    );
  }

  return (
    <NoResultsCard
      Icon={Bank}
      header="Get connected by filling out KYC"
      body="Integrate your bank account by filling out our KYC form."
    >
      <Button
        className="mb-10 mt-8 w-[200px]"
        onClick={() => setStartKYC(true)}
      >
        Start KYC
      </Button>
    </NoResultsCard>
  );
};

export default KYCOnboarding;
